import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, BookOpen, Loader2 } from "lucide-react";
import PublicShell, { WIDE } from "@/components/PublicShell";
import { api } from "@/lib/apiClient";
import { usePageMeta } from "@/lib/seo";
import { SIGN_FACTS, cap } from "@/lib/signFacts";
import { pairSlug } from "@/pages/CompatibilityPage";

export const SECTIONS = [["personality", "Personality"], ["love", "Love and relationships"], ["career", "Career and purpose"], ["money", "Money"],
  ["health", "Health and wellbeing"], ["friendship", "Friendship"], ["growth", "Growth and challenges"]];
const wrap = `px-5 pt-6 pb-10 fade-up ${WIDE ? "lg:max-w-3xl lg:mx-auto lg:pt-12" : ""}`;

export function SignGuidesIndex() {
  usePageMeta("Zodiac Sign Guides: All 12 Star Signs Explained · Astra", "/zodiac-signs",
    "In-depth guides to all 12 zodiac signs: personality, love, career, money, health, friendship and growth.");
  return (
    <PublicShell testId="sign-guides-index">
      <div className={wrap}>
        <p className="text-[11px] uppercase tracking-[0.3em] gold-text"><BookOpen size={11} className="inline -mt-0.5 mr-1" />Sign guides</p>
        <h1 className="text-4xl sm:text-5xl font-light mt-2 mb-6">The 12 zodiac signs</h1>
        <div className="grid gap-3 sm:grid-cols-2">
          {Object.entries(SIGN_FACTS).map(([s, f]) => (
            <Link key={s} to={`/signs/${s}`} data-testid={`sign-guide-card-${s}`} className="bg-[#0F1523]/90 border border-[#D4AF37]/25 hover:border-[#D4AF37]/60 rounded-2xl p-5 transition-colors">
              <p className="text-xl font-light">{cap(s)}</p>
              <p className="text-sm text-[#A7B0C0]">{f.dates} · {f.element} · ruled by {f.ruler}</p>
            </Link>
          ))}
        </div>
      </div>
    </PublicShell>
  );
}

export default function SignGuide() {
  const { sign = "" } = useParams();
  const s = sign.toLowerCase();
  const f = SIGN_FACTS[s];
  const [d, setD] = useState(null);
  const [error, setError] = useState(false);
  usePageMeta(f ? `${cap(s)} Zodiac Sign Guide: Personality, Love & Career · Astra` : "Sign guide · Astra", `/signs/${s}`,
    f && `The complete guide to ${cap(s)} (${f.dates}): personality, love, career, money, health, friendship and growth for this ${f.element.toLowerCase()} sign.`);
  useEffect(() => {
    if (!f) return;
    setD(null); setError(false);
    api.get(`/public/sign-guides/${s}`).then(({ data }) => setD(data)).catch(() => setError(true));
  }, [s, f]);
  if (!f) return <PublicShell testId="sign-guide"><p data-testid="sign-guide-not-found" className="text-center text-[#A7B0C0] py-24">This sign doesn't exist. <Link to="/zodiac-signs" className="gold-text">All signs</Link></p></PublicShell>;
  return (
    <PublicShell testId="sign-guide">
      <article className={wrap}>
        <p className="text-[11px] uppercase tracking-[0.3em] gold-text"><BookOpen size={11} className="inline -mt-0.5 mr-1" />Sign guide</p>
        <h1 data-testid="sign-guide-title" className="text-4xl sm:text-5xl font-light mt-2 mb-2">{cap(s)}: the complete guide</h1>
        <p className="text-base text-[#A7B0C0] mb-6">{f.dates} · {f.modality} {f.element} · ruled by {f.ruler} · symbol: {f.symbol}</p>
        {error && <p data-testid="sign-guide-error" className="text-[#A7B0C0]">This guide is being written. Please refresh in a minute.</p>}
        {!d && !error && <div className="flex justify-center py-12"><Loader2 className="animate-spin gold-text" size={26} /></div>}
        {d && SECTIONS.map(([k, label]) => (
          <section key={k} data-testid={`sign-guide-${k}`} className="mb-6">
            <h2 className="text-2xl font-light mb-2">{cap(s)} {label.toLowerCase()}</h2>
            {(d.sections[k] || "").split(/\n+/).filter(Boolean).map((p, i) => <p key={i} className="text-base leading-relaxed text-[#D6D0C5] mb-3">{p}</p>)}
          </section>
        ))}
        <nav aria-label={`More about ${cap(s)}`} className="mt-6 flex flex-wrap gap-2" data-testid="sign-guide-links">
          {[[`/s/${s}/daily`, `${cap(s)} horoscope today`], [`/s/${s}/weekly`, "This week"], [`/s/${s}/monthly`, "This month"],
            ...f.matches.map((m) => [`/compatibility/${pairSlug(s, m)}`, `${cap(s)} × ${cap(m)}`])].map(([to, label]) => (
            <Link key={to} to={to} className="text-sm border border-[#D4AF37]/30 hover:bg-[#D4AF37]/10 text-[#E8DCC4] rounded-full px-4 py-2 transition-colors">{label}</Link>
          ))}
        </nav>
        <Link to="/readings/natal" data-testid="sign-guide-cta" className="mt-6 flex items-center justify-between bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium rounded-full px-6 py-3.5 transition-colors">
          Go beyond your Sun sign with a Natal Chart reading <ArrowRight size={17} />
        </Link>
        <p className="text-xs text-[#A7B0C0] mt-4"><Link to="/zodiac-signs" className="gold-text">All 12 sign guides</Link> · <Link to="/editorial-policy" className="gold-text">How we create content</Link></p>
      </article>
    </PublicShell>
  );
}
